import { useEffect, useRef } from 'react'
import type { CSSProperties } from 'react'
import type { DatasetCatalogueEntry, Locale } from '../data/schema'
import { localize, localizeWithEnglish } from '../i18n'
import { Icon } from './Icon'

interface TraditionSelectorProps {
  locale: Locale
  datasets: DatasetCatalogueEntry[]
  activeId: string
  required?: boolean
  onSelect: (id: string) => void
  onClose?: () => void
}

export function TraditionSelector({ locale, datasets, activeId, required = false, onSelect, onClose }: TraditionSelectorProps) {
  const activeButtonRef = useRef<HTMLButtonElement>(null)
  const copy = locale === 'en' ? {
    eyebrow: 'Choose a tradition',
    title: 'Which mythology would you like to enter?',
    body: 'Each atlas keeps its own people, stories, sources and timeline. You can return here whenever you want to change traditions.',
    current: 'Current tradition',
    unavailable: 'Not yet available',
    close: 'Close mythology selector',
  } : {
    eyebrow: '選擇神話傳統',
    title: '你想從哪一個神話開始？',
    body: '每張神話地圖保留各自的人物、故事、來源與時間線；之後隨時都可以回來切換。',
    current: '目前神話',
    unavailable: '尚未開放',
    close: '關閉神話選擇器',
  }

  useEffect(() => {
    activeButtonRef.current?.focus()
  }, [])

  useEffect(() => {
    if (required || !onClose) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose, required])

  return <div className="tradition-selector-layer" role="presentation">
    <section className="tradition-selector-modal" role="dialog" aria-modal="true" aria-labelledby="tradition-selector-title" data-testid="tradition-selector-modal">
      <header>
        <div>
          <span className="eyebrow">{copy.eyebrow}</span>
          <h2 id="tradition-selector-title">{copy.title}</h2>
        </div>
        {!required && onClose && <button className="tradition-selector-close" type="button" onClick={onClose} aria-label={copy.close}><Icon name="close" size={17} /></button>}
      </header>
      <p>{copy.body}</p>
      <div className="tradition-selector-options" aria-label={locale === 'en' ? 'Available mythologies' : '可選神話'}>
        {datasets.map((entry) => {
          const active = entry.id === activeId
          const available = entry.status === 'available'
          const style = {
            '--selector-accent': entry.accent.accent,
            '--selector-accent-soft': entry.accent.accentSoft,
            '--selector-accent-muted': entry.accent.accentMuted,
            '--selector-panel-tint': entry.accent.panelTint,
          } as CSSProperties
          return <button
            key={entry.id}
            ref={active ? activeButtonRef : undefined}
            type="button"
            className={`tradition-option${active ? ' is-active' : ''}`}
            style={style}
            disabled={!available}
            aria-pressed={active}
            onClick={() => onSelect(entry.id)}
          >
            <span className="tradition-option-mark"><Icon name="spark" size={19} /></span>
            <span className="tradition-option-copy">
              <strong>{localizeWithEnglish(entry.name, locale)}</strong>
              <small>{active ? copy.current : available ? localize(entry.shortName, locale) : copy.unavailable}</small>
              <em>{localize(entry.description, locale)}</em>
            </span>
            <Icon name={available ? 'arrow-right' : 'close'} size={18} />
          </button>
        })}
      </div>
    </section>
  </div>
}
